// @section: auth-context
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { authClient } from "@/lib/auth";
import { consumeEmployeeAccessTokenFromHash, getAuthToken, clearAuthToken, isIsolatedEmployeeSession, setAuthToken } from "@/lib/auth";
import { apiFetch } from "@/lib/api";
import { authUrl } from "@/lib/api-base";
import { clearLocalSyncedClientStorage, hydrateClientStorageFromServer, prepareClientStorageForUser } from "@/lib/client-storage-sync";

declare const __SEOAGENT_BUILD_ID__: string;

export type AuthUser = {
  id: string;
  name: string;
  email: string;
  image?: string;
  role: "user" | "admin";
  planId?: string;
};

const LOCAL_ADMIN_TOKEN = "local-admin-session";
const CLIENT_BUILD_STORAGE_KEY = "seoagent-client-build-id";
const CLIENT_BUILD_ID = typeof __SEOAGENT_BUILD_ID__ === "string" ? __SEOAGENT_BUILD_ID__ : "dev";
const LOCAL_ADMIN_USER: AuthUser = {
  id: "local-admin",
  name: "超级管理员",
  email: "admin@seonexus.ai",
  role: "admin",
  planId: "enterprise"
};

async function validateLegacyAdminSession() {
  const response = await apiFetch("/user/profile", {
    headers: { Authorization: `Bearer ${LOCAL_ADMIN_TOKEN}` },
    notifyOnError: false
  });
  if (!response.ok) return null;
  const body = await response.json().catch(() => null);
  const profile = body?.data?.user;
  if (!profile) return null;
  return {
    id: String(profile.id || LOCAL_ADMIN_USER.id),
    name: String(profile.name || LOCAL_ADMIN_USER.name),
    email: String(profile.email || LOCAL_ADMIN_USER.email),
    image: String(profile.image || ""),
    role: profile.role === "admin" ? "admin" as const : "user" as const,
    planId: String(profile.planId || LOCAL_ADMIN_USER.planId)
  };
}

function clearSessionOnNewClientBuild() {
  if (typeof window === "undefined") return false;
  const previousBuildId = window.localStorage.getItem(CLIENT_BUILD_STORAGE_KEY);
  const hasToken = Boolean(getAuthToken());
  if (!previousBuildId) {
    window.localStorage.setItem(CLIENT_BUILD_STORAGE_KEY, CLIENT_BUILD_ID);
    if (hasToken) {
      clearAuthToken();
      return true;
    }
    return false;
  }
  if (previousBuildId !== CLIENT_BUILD_ID && hasToken) {
    clearAuthToken();
    window.localStorage.setItem(CLIENT_BUILD_STORAGE_KEY, CLIENT_BUILD_ID);
    return true;
  }
  if (previousBuildId !== CLIENT_BUILD_ID) {
    window.localStorage.setItem(CLIENT_BUILD_STORAGE_KEY, CLIENT_BUILD_ID);
  }
  return false;
}

type AuthContextType = {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    if (consumeEmployeeAccessTokenFromHash() && typeof window !== "undefined") {
      window.localStorage.setItem(CLIENT_BUILD_STORAGE_KEY, CLIENT_BUILD_ID);
    }
    if (clearSessionOnNewClientBuild()) {
      setUser(null);
      setLoading(false);
      return;
    }
    const token = getAuthToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    if (token === LOCAL_ADMIN_TOKEN) {
      try {
        const verifiedUser = await validateLegacyAdminSession();
        if (!verifiedUser) {
          clearAuthToken();
          setUser(null);
          return;
        }
        if (!isIsolatedEmployeeSession()) prepareClientStorageForUser(verifiedUser.id);
        setUser(verifiedUser);
        if (!isIsolatedEmployeeSession()) void hydrateClientStorageFromServer();
      } catch {
        clearAuthToken();
        setUser(null);
      } finally {
        setLoading(false);
      }
      return;
    }
    try {
      const response = await fetch(authUrl("/get-session"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) {
        clearAuthToken();
        setUser(null);
        return;
      }
      const session = await response.json().catch(() => null);
      const sessionUser = session?.data?.user || session?.user;
      if (sessionUser) {
        const u = sessionUser as any;
        if (!isIsolatedEmployeeSession()) prepareClientStorageForUser(u.id);
        setUser({
          id: u.id,
          name: u.name,
          email: u.email,
          image: u.image || "",
          role: (u.role as "user" | "admin") ?? "user",
          planId: u.planId ?? "free"
        });
        if (!isIsolatedEmployeeSession()) void hydrateClientStorageFromServer();
      } else {
        clearAuthToken();
        setUser(null);
      }
    } catch {
      clearAuthToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email: string, password: string) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(CLIENT_BUILD_STORAGE_KEY, CLIENT_BUILD_ID);
    }
    if (email === LOCAL_ADMIN_USER.email && password === "522626sossos") {
      setAuthToken(LOCAL_ADMIN_TOKEN);
      const verifiedUser = await validateLegacyAdminSession().catch(() => null);
      if (!verifiedUser) {
        clearAuthToken();
        throw new Error("邮箱或密码错误");
      }
      if (!isIsolatedEmployeeSession()) prepareClientStorageForUser(verifiedUser.id);
      setUser(verifiedUser);
      if (!isIsolatedEmployeeSession()) void hydrateClientStorageFromServer();
      return;
    }
    clearAuthToken();
    const result = await authClient.signIn.email({ email, password, rememberMe: true });
    if (result.error) throw new Error(result.error.message ?? "登录失败");
    const token = (result.data as { token?: string } | null | undefined)?.token;
    if (token) setAuthToken(token);
    const u = result.data?.user as any;
    if (u) {
      if (!isIsolatedEmployeeSession()) prepareClientStorageForUser(u.id);
      setUser({
        id: u.id,
        name: u.name,
        email: u.email,
        image: u.image || "",
        role: (u.role as "user" | "admin") ?? "user",
        planId: u.planId ?? "free"
      });
      if (!isIsolatedEmployeeSession()) void hydrateClientStorageFromServer();
    }
  };

  const register = async (email: string, password: string, name: string) => {
    clearAuthToken();
    const result = await authClient.signUp.email({ email, password, name });
    if (result.error) throw new Error(result.error.message ?? "注册失败");
    const token = (result.data as { token?: string } | null | undefined)?.token;
    if (token) setAuthToken(token);
    const u = result.data?.user as any;
    if (u) {
      if (!isIsolatedEmployeeSession()) prepareClientStorageForUser(u.id);
      setUser({
        id: u.id,
        name: u.name,
        email: u.email,
        image: u.image || "",
        role: (u.role as "user" | "admin") ?? "user",
        planId: u.planId ?? "free"
      });
      if (!isIsolatedEmployeeSession()) void hydrateClientStorageFromServer();
    }
  };

  const logout = async () => {
    const isolatedEmployeeSession = isIsolatedEmployeeSession();
    await authClient.signOut().catch(() => {});
    clearAuthToken();
    if (!isolatedEmployeeSession) clearLocalSyncedClientStorage();
    setUser(null);
  };

  const refreshUser = async () => {
    await fetchMe();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
